import React, { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, InputNumber, message, Modal, Popconfirm, Segmented, Table, Tag, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../hooks/useAuthStore';
import { useProjectStore } from '../../hooks/useProjectStore';
import {
  getApiErrorMessage,
  labAPI,
  type LabExperiment,
  type LabExperimentType,
  type LabField,
  type LabImportResult,
  type LabRecommendation,
} from '../../services/api';
import { useAgentCommands } from '../../services/agent/registry';
import { useT } from '../../i18n';

// ═══════════════════════════════════════════════
// 物模实验库: 物理模拟实验数据导入 → 与当前井条件对标 → 设计参考
//
// 对标、排序、插值都是后端确定性计算 (apps/labdata/analysis.py);
// 「让爱压解读」只是把结果交给大模型做文字解释, 不让它挑数。
// ═══════════════════════════════════════════════

const TH = { text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD', border: '#D6DAE0', soft: '#ECEEF1', accent: '#C2533A' };
const WRITER_ROLES = new Set(['admin', 'engineer']);

const fmt = (value: unknown) => (typeof value === 'number' ? (Math.abs(value) >= 100 ? value.toFixed(0) : String(Number(value.toFixed(3)))) : (value as string) ?? '');

export default function LabExperimentPage() {
  const t = useT();
  const { user } = useAuthStore();
  const { currentProject } = useProjectStore();
  const canWrite = WRITER_ROLES.has(user?.role ?? '');

  const [types, setTypes] = useState<LabExperimentType[]>([]);
  const [typeKey, setTypeKey] = useState('true_triaxial_frac');
  const [rows, setRows] = useState<LabExperiment[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<LabImportResult | null>(null);
  const [target, setTarget] = useState<Record<string, number | string>>({});
  const [depthRange, setDepthRange] = useState<{ top?: number | null; bottom?: number | null }>({});
  const [recommendation, setRecommendation] = useState<LabRecommendation | null>(null);
  const [analysing, setAnalysing] = useState(false);

  const spec = types.find((item) => item.key === typeKey);

  useEffect(() => {
    labAPI.schema().then(({ data }) => setTypes(data)).catch((error) => message.error(getApiErrorMessage(error, t('读取实验库字段失败'))));
  }, []);

  const load = (q = query) => {
    setLoading(true);
    labAPI.list(typeKey, q.trim() || undefined)
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch((error) => { setRows([]); message.error(getApiErrorMessage(error, t('读取实验数据失败'))); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); setRecommendation(null); setTarget({}); }, [typeKey]);

  const downloadTemplate = async () => {
    try {
      const { data } = await labAPI.template(typeKey);
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lab-template-${typeKey}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(getApiErrorMessage(error, t('模板下载失败')));
    }
  };

  const importFile = async (file: File) => {
    setImporting(true);
    try {
      const { data } = await labAPI.importFile(typeKey, file);
      setImportResult(data);
      load();
    } catch (error) {
      message.error(getApiErrorMessage(error, t('导入失败')));
    } finally {
      setImporting(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await labAPI.remove(id);
      setRows((current) => current.filter((row) => row.id !== id));
    } catch (error) {
      message.error(getApiErrorMessage(error, t('删除失败')));
    }
  };

  const fillFromProject = async () => {
    if (!currentProject?.id) return void message.warning(t('请先选择项目'));
    try {
      const { data } = await labAPI.projectConditions(currentProject.id, depthRange.top, depthRange.bottom);
      const allowed = new Set(spec?.conditions.map((f) => f.key));
      const picked = Object.fromEntries(Object.entries(data.conditions).filter(([key]) => allowed.has(key)));
      if (!Object.keys(picked).length) {
        message.info(t('当前项目没有可用于这类实验的力学/压力数据, 请手动填写'));
        return;
      }
      setTarget((current) => ({ ...current, ...picked }));
      message.success(t('已带入 {n} 项井条件 (来自 {m} 个力学数据点)', { n: Object.keys(picked).length, m: data.sample_count }));
    } catch (error) {
      message.error(getApiErrorMessage(error, t('读取项目数据失败')));
    }
  };

  const analyse = async () => {
    setAnalysing(true);
    try {
      const { data } = await labAPI.recommend(typeKey, target);
      setRecommendation(data);
      return data;
    } catch (error) {
      message.error(getApiErrorMessage(error, t('对标分析失败')));
      return null;
    } finally {
      setAnalysing(false);
    }
  };

  const askAI = (rec: LabRecommendation) => {
    const top = rec.similar.slice(0, 5).map((e) => `${e.code} (${t('相似度')} ${e.similarity}%)`).join('、');
    const prompt = [
      t('请解读以下物模实验对标结果, 说明对当前井压裂设计的指导意义, 并指出室内与现场的尺度差异和需要复核的地方。'),
      `${t('实验类型')}: ${t(spec?.label ?? typeKey)}`,
      `${t('目标条件')}: ${JSON.stringify(rec.target)}`,
      `${t('相似实验')}: ${top || t('无')}`,
      ...rec.guidance.map((line) => `- ${line}`),
    ].join('\n');
    window.dispatchEvent(new CustomEvent('fracsight-ai-open', { detail: { prompt } }));
  };

  const downloadConductivityTables = (rec: LabRecommendation) => {
    const blob = new Blob([JSON.stringify(rec.conductivity_tables, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'lab-conductivity-tables.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  // 爱压 AI 操作模式
  useAgentCommands({
    'lab.recommend': async () => {
      const rec = await analyse();
      if (!rec) throw new Error('对标分析失败');
      return rec.guidance.join('\n');
    },
  }, () => `物模实验库: ${spec?.label ?? typeKey}, 共 ${rows.length} 条实验; 目标条件 ${JSON.stringify(target)}`);

  const fieldColumns = (fields: LabField[], group: 'conditions' | 'results') => fields.map((f) => ({
    title: <span>{t(f.label)}{f.unit && <span style={{ color: TH.textMuted, fontWeight: 400 }}> {f.unit}</span>}</span>,
    key: `${group}.${f.key}`,
    width: f.kind === 'number' ? 96 : 120,
    render: (_: unknown, row: LabExperiment) => fmt(row[group][f.key]),
  }));

  const columns = useMemo(() => (spec ? [
    { title: t('实验编号'), dataIndex: 'code', width: 110, fixed: 'left' as const },
    { title: t('层位'), dataIndex: 'formation', width: 90 },
    { title: t('岩性'), dataIndex: 'lithology', width: 80 },
    ...fieldColumns(spec.conditions, 'conditions'),
    ...fieldColumns(spec.results, 'results'),
    { title: t('实验室'), dataIndex: 'lab', width: 100 },
    ...(canWrite ? [{
      title: '', key: 'ops', width: 50, fixed: 'right' as const,
      render: (_: unknown, row: LabExperiment) => (
        <Popconfirm title={t('删除这条实验?')} onConfirm={() => void remove(row.id)} okText={t('删除')} cancelText={t('取消')}>
          <Button size="small" type="text" danger>×</Button>
        </Popconfirm>
      ),
    }] : []),
  ] : []), [spec, canWrite, t]);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#fff' }}>
      <header style={{ padding: '12px 20px', borderBottom: `1px solid ${TH.border}` }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: TH.text }}>{t('物模实验库')}</div>
        <div style={{ fontSize: 12, color: TH.textMuted, marginTop: 2 }}>
          {t('物理模拟实验数据统一入库, 与当前井条件对标, 为压裂设计提供实验依据; 实验库全公司共享')}
        </div>
      </header>
      <div style={{ padding: '10px 20px', borderBottom: `1px solid ${TH.soft}` }}>
        <Segmented value={typeKey} onChange={(v) => setTypeKey(String(v))}
          options={types.map((item) => ({ value: item.key, label: t(item.label) }))} />
        {spec && <span style={{ fontSize: 11.5, color: TH.textMuted, marginLeft: 12 }}>{t(spec.description)}</span>}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 420px' }}>
        <section style={{ display: 'flex', flexDirection: 'column', minWidth: 0, padding: '12px 16px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <Input.Search allowClear placeholder={t('按编号、层位、岩性、实验室搜索')} style={{ maxWidth: 320 }}
              value={query} onChange={(e) => setQuery(e.target.value)} onSearch={(v) => load(v)} />
            <span style={{ flex: 1 }} />
            <Button icon={<DownloadOutlined />} onClick={() => void downloadTemplate()}>{t('下载导入模板')}</Button>
            <Upload accept=".xlsx,.xls,.csv" showUploadList={false} disabled={!canWrite}
              beforeUpload={(file) => { void importFile(file); return false; }}>
              <Button type="primary" icon={<UploadOutlined />} loading={importing} disabled={!canWrite}
                title={canWrite ? undefined : t('只有工程师或管理员可以导入')} style={{ background: canWrite ? TH.accent : undefined }}>
                {t('导入实验数据')}
              </Button>
            </Upload>
          </div>
          <Table
            size="small" rowKey="id" loading={loading} columns={columns} dataSource={rows}
            scroll={{ x: 'max-content', y: 'calc(100vh - 260px)' }}
            pagination={{ pageSize: 50, size: 'small', showTotal: (n) => t('共 {n} 条', { n }) }}
            locale={{ emptyText: <Empty description={t('还没有这一类实验数据, 下载模板整理后导入')} /> }}
          />
        </section>

        <aside style={{ borderLeft: `1px solid ${TH.border}`, overflowY: 'auto', padding: 14, background: '#FAFBFC' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TH.text, marginBottom: 4 }}>{t('对标当前井 · 设计参考')}</div>
          <div style={{ fontSize: 11, color: TH.textMuted, marginBottom: 10 }}>{t('填写目标条件, 找出条件最接近的实验并总结规律')}</div>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
            <InputNumber size="small" placeholder={t('顶深')} value={depthRange.top ?? null} style={{ width: 90 }}
              onChange={(v) => setDepthRange((r) => ({ ...r, top: v }))} />
            <InputNumber size="small" placeholder={t('底深')} value={depthRange.bottom ?? null} style={{ width: 90 }}
              onChange={(v) => setDepthRange((r) => ({ ...r, bottom: v }))} />
            <Button size="small" onClick={() => void fillFromProject()} disabled={!currentProject}>{t('从项目数据带入')}</Button>
          </div>

          {spec?.conditions.map((f) => (
            <label key={f.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 11.5, color: TH.textSec, marginBottom: 6 }}>
              <span>{t(f.label)}{f.unit && <span style={{ color: TH.textMuted }}> ({f.unit})</span>}</span>
              {f.kind === 'number' ? (
                <InputNumber size="small" style={{ width: 130 }} value={typeof target[f.key] === 'number' ? (target[f.key] as number) : null}
                  onChange={(v) => setTarget((cur) => {
                    const next = { ...cur };
                    if (v == null) delete next[f.key]; else next[f.key] = Number(v);
                    return next;
                  })} />
              ) : (
                <Input size="small" style={{ width: 130 }} value={(target[f.key] as string) ?? ''}
                  onChange={(e) => setTarget((cur) => ({ ...cur, [f.key]: e.target.value }))} />
              )}
            </label>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11.5, color: TH.textSec, marginBottom: 10 }}>
            <span>{t('层位 (同层位优先)')}</span>
            <Input size="small" style={{ width: 130 }} value={(target.formation as string) ?? ''}
              onChange={(e) => setTarget((cur) => ({ ...cur, formation: e.target.value }))} />
          </label>
          <Button type="primary" block loading={analysing} onClick={() => void analyse()} style={{ background: TH.accent }}>{t('对标分析')}</Button>

          {recommendation && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: TH.text, marginBottom: 6 }}>{t('设计参考')}</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: TH.text, lineHeight: 1.7 }}>
                {recommendation.guidance.map((line, i) => <li key={i}>{line}</li>)}
              </ul>

              {recommendation.ranking.length > 0 && (
                <>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: TH.text, margin: '12px 0 6px' }}>{t('目标闭合应力下的导流能力排序')}</div>
                  <Table size="small" pagination={false} rowKey={(r) => `${r.proppant_type}-${r.mesh}-${r.concentration}`}
                    dataSource={recommendation.ranking}
                    columns={[
                      { title: t('支撑剂'), render: (_, r) => `${r.proppant_type} ${r.mesh}` },
                      { title: 'kg/m²', dataIndex: 'concentration', width: 60, render: (v) => v ?? '—' },
                      { title: 'D·mm', dataIndex: 'conductivity_at_target', width: 80,
                        render: (v, r) => <span>{v}{r.extrapolated && <Tag color="orange" style={{ marginLeft: 4 }}>{t('外推')}</Tag>}</span> },
                    ]} />
                  {recommendation.conductivity_tables.length > 0 && (
                    <Button size="small" block style={{ marginTop: 6 }} onClick={() => downloadConductivityTables(recommendation)}>
                      {t('导出导流能力表 (压裂模拟格式)')}
                    </Button>
                  )}
                </>
              )}

              <div style={{ fontSize: 12.5, fontWeight: 700, color: TH.text, margin: '12px 0 6px' }}>{t('相似实验')}</div>
              {recommendation.similar.length === 0 && <div style={{ fontSize: 12, color: TH.textMuted }}>{t('没有符合条件的实验')}</div>}
              {recommendation.similar.map((e) => (
                <div key={e.id} style={{ background: '#fff', border: `1px solid ${TH.soft}`, borderRadius: 6, padding: '6px 8px', marginBottom: 6, fontSize: 11.5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b style={{ color: TH.text }}>{e.code}{e.formation && <span style={{ fontWeight: 400, color: TH.textMuted }}> · {e.formation}</span>}</b>
                    <span style={{ color: TH.accent, fontWeight: 700 }}>{e.similarity}%</span>
                  </div>
                  <div style={{ color: TH.textSec, marginTop: 2 }}>
                    {Object.entries(e.results).slice(0, 4).map(([key, value]) => {
                      const f = spec?.results.find((item) => item.key === key);
                      return <span key={key} style={{ marginRight: 10 }}>{t(f?.label ?? key)} {fmt(value)}{f?.unit ? ` ${f.unit}` : ''}</span>;
                    })}
                  </div>
                </div>
              ))}

              <Button block onClick={() => askAI(recommendation)} style={{ marginTop: 6 }}>{t('让爱压解读')}</Button>
            </div>
          )}
        </aside>
      </div>

      <Modal open={Boolean(importResult)} title={t('导入结果')} onCancel={() => setImportResult(null)} onOk={() => setImportResult(null)}
        okText={t('知道了')} cancelButtonProps={{ style: { display: 'none' } }}>
        {importResult && (
          <div style={{ fontSize: 13 }}>
            <p>{t('新增 {a} 条, 更新 {b} 条, 跳过 {c} 行。', { a: importResult.created, b: importResult.updated, c: importResult.error_count })}</p>
            {importResult.unknown_headers.length > 0 && (
              <p style={{ color: TH.textSec }}>{t('未识别的列 (已忽略)')}: {importResult.unknown_headers.join('、')}</p>
            )}
            {importResult.errors.length > 0 && (
              <div style={{ maxHeight: 240, overflowY: 'auto', background: '#FFF6F1', padding: 8, borderRadius: 4 }}>
                {importResult.errors.map((e) => (
                  <div key={e.row}>{t('第 {row} 行', { row: e.row })} {e.code && `(${e.code})`}: {e.errors.join('; ')}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
